import {
  isNavMenuItem,
  NavMenuItem,
} from "types/CMS/KontentAI/content-types/menu_item";

import {
  isNavInternalLink,
  NavInternalLink,
} from "types/CMS/KontentAI/content-types/internal_link";
import {
  isNavExternalLink,
  NavExternalLink,
} from "types/CMS/KontentAI/content-types/external_link";
import {
  NavSubItem,
  isNavSubItem,
} from "types/CMS/KontentAI/content-types/menu_sub_item";
import {
  isNavMenuItemGroup,
  NavMenuItemGroup,
} from "types/CMS/KontentAI/content-types/menu_item_group";
import { createResolver } from "../Factories/ResolverFactory";
import { navLink } from "types/CMS/Unum/Navbar/navLink";
import { linkResolver } from "../link";
import { isLinkExternal } from "types/CMS/Unum/link";
import { navDraw } from "types/CMS/Unum/Navbar/navDraw";
import { navItem } from "types/CMS/Unum/Navbar/navbar";
import { iconReslover } from "./icon";
import { KontentFilterable } from "types/CMS/Unum/filterable";
import { productTaxonomyResolver } from "../products";
import { roleTaxonomyResolver } from "../roles";
import { IconName } from "assets/icons/UnumSync";
import { Elements } from "@kontent-ai/delivery-sdk";
import { Roles } from "types/CMS/KontentAI/taxonomies/roles";
import { Products } from "types/CMS/KontentAI";
import { productTaxonomyFilter } from "../Filters/productTaxomanyFilter";
import { roleTaxonomyFilter } from "../Filters/roleTaxomanyFilter";

export const menuArrayResolver = createResolver({
  resolve: (
    menuItems: (NavMenuItem | NavMenuItemGroup)[],
    filters,
  ): (navItem & KontentFilterable)[] => {
    const items = menuItems
      .map((item) => menuResolver.resolve(item, filters).result)
      .filter((i) => i !== undefined);
    return items;
  },
});

const menuResolver = createResolver({
  resolve: (
    item: NavMenuItem | NavMenuItemGroup,
    filters,
  ): (navItem & KontentFilterable) | undefined => {
    if (isNavMenuItem(item)) {
      return menuItemResolver.resolve(item).result;
    }
    if (isNavMenuItemGroup(item)) {
      return menuItemGroupResolver.resolve(item, filters).result;
    }
    return undefined;
  },
});

// this block can be removed after Kyan update the submenu to be the only allowed item
type subMenuItems =
  | NavMenuItem
  | NavInternalLink
  | NavExternalLink
  | NavSubItem;

const extractLinkFromItem = (
  item: subMenuItems,
): NavInternalLink | NavExternalLink => {
  if (isNavMenuItem(item) || isNavSubItem(item)) {
    return item?.elements?.link?.linkedItems[0];
  }
  if (isNavInternalLink(item) || isNavExternalLink(item)) {
    return item;
  }
  throw new Error("Unknown item type");
};

const extractRoleFromItem = (
  item: subMenuItems,
): Elements.TaxonomyElement<Roles, "roles"> | undefined => {
  if (isNavMenuItem(item) || isNavSubItem(item)) {
    return item?.elements?.roles;
  }
  return undefined;
};

const extractProductFromItem = (
  item: subMenuItems,
): Elements.TaxonomyElement<Products, "products"> | undefined => {
  if (isNavMenuItem(item) || isNavSubItem(item)) {
    return item?.elements?.products;
  }
  return undefined;
};
// this block can be removed after Kyan update the submenu to be the only allowed item

const menuItemResolver = createResolver({
  resolve: (
    menuItem: NavMenuItem | NavInternalLink | NavExternalLink | NavSubItem,
  ): navLink & KontentFilterable => {
    let icon: IconName | undefined = undefined;

    const link = linkResolver.resolve(extractLinkFromItem(menuItem)).result;

    const extractedRole = extractRoleFromItem(menuItem);
    const role = extractedRole
      ? roleTaxonomyResolver.resolve(extractedRole).result
      : [];

    const extractedProduct = extractProductFromItem(menuItem);
    const product = extractedProduct
      ? productTaxonomyResolver.resolve(extractedProduct).result
      : [];

    if (isNavMenuItem(menuItem)) {
      icon = iconReslover.resolve(menuItem.elements.icon).result;
    }

    return {
      isFilterable: true,
      productTaxonomy: product,
      roleTaxonomy: role,
      id: menuItem.system.id,
      icon: icon,
      type: isLinkExternal(link) ? "external" : "internal",
      label: link.label,
      link: link.url,
    };
  },
});

const menuItemGroupResolver = createResolver({
  resolve: (
    menuItemGroup: NavMenuItemGroup,
    filters,
  ): navDraw & KontentFilterable => {
    return {
      isFilterable: true,
      productTaxonomy: productTaxonomyResolver.resolve(
        menuItemGroup.elements.products,
      ).result,
      roleTaxonomy: roleTaxonomyResolver.resolve(menuItemGroup.elements.roles)
        .result,
      id: menuItemGroup.system.id,
      type: "draw",
      label: menuItemGroup.elements.label.value,
      icon: iconReslover.resolve(menuItemGroup.elements.icon).result,
      items: menuItemGroup.elements.children.linkedItems
        .map((item) => menuItemResolver.resolve(item).result)
        .filter(
          (resolved): resolved is navLink & KontentFilterable =>
            resolved !== undefined &&
            productTaxonomyFilter(resolved, filters?.usersProducts) &&
            roleTaxonomyFilter(resolved, filters?.usersRoles),
        ),
    };
  },
});
